import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import { withStyles } from "@mui/styles";
import { FunctionComponent } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { BACKGROUND_COLORS } from "../constants/map";
import styles from "./OverrideSelector.module.css";

interface Props {
  streetLabelSpacing: number;
  backgroundColor: string;
  hiddenPaths: boolean;
  onStreetLabelSpacingUpdate: (value: number) => any;
  onBackgroundColorUpdate: (color: keyof typeof BACKGROUND_COLORS) => any;
  onHiddenPathsUpdate: (hidden: boolean) => any;
}

const MySlider: any = withStyles(() => ({
  root: {
    width: "auto",
    height: 8,
  },
  thumb: {
    height: 24,
    width: 24,
    backgroundColor: "#fff",
    border: "2px solid currentColor",
    "&:focus,&:hover,&.Mui-active": {
      boxShadow: "inherit",
    },
  },
  active: {},
  valueLabel: {
    left: "calc(-50% + 4px)",
  },
  track: {
    height: 8,
    borderRadius: 4,
  },
  rail: {
    height: 8,
    borderRadius: 4,
    opacity: 1,
  },
  mark: {
    display: "none",
  },
  markLabel: {
    top: 32,
  },
}))(Slider);

const SPACING_MIN = 500;
const SPACING_MAX = 900;
const SPACING_STEP = 20;

export const OverrideSelector: FunctionComponent<Props> = ({
  streetLabelSpacing,
  backgroundColor,
  hiddenPaths,
  onStreetLabelSpacingUpdate,
  onBackgroundColorUpdate,
  onHiddenPathsUpdate,
}) => {
  const { t } = useTranslation();
  const TooltipAny: any = Tooltip;
  const FormControlAny: any = FormControl;
  const FormLabelAny: any = FormLabel;
  const FormGroupAny: any = FormGroup;
  const FormControlLabelAny: any = FormControlLabel;
  const SwitchAny: any = Switch;
  const IconButtonAny: any = IconButton;

  return (
    <>
      <FormControlAny fullWidth margin="normal">
        <FormLabelAny>{t("overrides_backgroundColor")}</FormLabelAny>
        <ToggleButtonGroup
          className={styles.colorGrid}
          value={backgroundColor}
          onChange={(e, value) => {
            onBackgroundColorUpdate(value);
          }}
          exclusive
          sx={{
            "& .MuiToggleButtonGroup-firstButton": {
              borderRadius: "50%",
            },
            "& .MuiToggleButtonGroup-middleButton": {
              borderRadius: "50%",
              borderLeft: "1px solid var(--border-color)",
            },
            "& .MuiToggleButtonGroup-lastButton": {
              borderRadius: "50%",
              borderLeft: "1px solid var(--border-color)",
            },
            "& .MuiToggleButton-root": {
              padding: "0",
            },
          }}
        >
          {Object.entries(BACKGROUND_COLORS).map(([name, color]) => (
            <ToggleButton
              key={name}
              value={color}
              classes={{
                root: styles.colorSwatch,
                selected: styles.colorSwatchSelected,
              }}
              className={styles.colorSwatch}
            >
              <TooltipAny title={t(`color__${name}`)}>
                <div
                  className={styles.colorSwatch__preview}
                  style={{ backgroundColor: color }}
                />
              </TooltipAny>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </FormControlAny>
      {!isMobile && (
        <>
          <FormControlAny fullWidth margin="normal">
            <FormLabelAny>{t("overrides_streetLabelPlacement")}</FormLabelAny>
            <div className={styles.sliderGrid}>
              <IconButtonAny
                size="small"
                disabled={streetLabelSpacing === SPACING_MIN}
                onClick={() =>
                  onStreetLabelSpacingUpdate(
                    Math.max(streetLabelSpacing - 20, SPACING_MIN)
                  )
                }
              >
                <ArrowBack />
              </IconButtonAny>
              <MySlider
                value={streetLabelSpacing}
                step={SPACING_STEP}
                min={SPACING_MIN}
                max={SPACING_MAX}
                onChange={(e, value) =>
                  onStreetLabelSpacingUpdate(value as number)
                }
              />
              <IconButtonAny
                size="small"
                disabled={streetLabelSpacing === SPACING_MAX}
                onClick={() =>
                  onStreetLabelSpacingUpdate(
                    Math.min(streetLabelSpacing + 20, SPACING_MAX)
                  )
                }
              >
                <ArrowForward />
              </IconButtonAny>
            </div>
          </FormControlAny>
          <FormControlAny fullWidth margin="normal">
            <FormLabelAny>{t("overrides_backgroundObjects")}</FormLabelAny>
            <FormGroupAny>
              <FormControlLabelAny
                control={
                  <SwitchAny
                    color="primary"
                    checked={hiddenPaths}
                    onChange={(e) => onHiddenPathsUpdate(e.target.checked)}
                  />
                }
                label={t("overrides_hiddenPaths")}
                labelPlacement="end"
              />
            </FormGroupAny>
          </FormControlAny>
        </>
      )}
    </>
  );
};
